/**
 * PoLineTraceabilityModal.tsx
 *
 * Giai đoạn 7 / Mục G3 — UI Truy vết ngược nguồn gốc BOM trên dòng PO.
 * Hiển thị chi tiết đợt nhu cầu (batch), so sánh SL mua thực tế vs SL BOM tính toán,
 * và bảng breakdown từng mã hàng đóng góp (KHÔNG gộp dòng/merge cell).
 */
import { X, GitFork, AlertCircle, CheckCircle2, AlertTriangle, Layers } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { formatDateFull } from '@/lib/dateFormat'
import { formatVnNumber } from '@/features/purchasing/poWorkflow'
import type { PoLineTraceabilityResult } from '@/services/procurement/materialRequirementApiClient'

export interface PoLineTraceabilityModalProps {
  isOpen: boolean
  onClose: () => void
  data: PoLineTraceabilityResult | null
  loading?: boolean
  error?: string | null
}

function getBomSourceLabel(source: string): string {
  switch (source?.toLowerCase()) {
    case 'foam':
      return 'Mút xốp'
    case 'hardware':
      return 'Kim khí'
    case 'wood':
      return 'Gỗ'
    default:
      return source || 'Khác'
  }
}

export function PoLineTraceabilityModal({
  isOpen,
  onClose,
  data,
  loading = false,
  error = null,
}: PoLineTraceabilityModalProps) {
  if (!isOpen) return null

  const totalContribution = data?.breakdown?.reduce(
    (sum, item) => sum + (Number(item.contribution_qty) || 0),
    0,
  ) ?? 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 bg-slate-50/70">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
              <GitFork className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">
                Truy vết nguồn gốc BOM — Dòng PO
              </h2>
              <p className="text-xs text-slate-500">
                Chi tiết liên kết đợt nhu cầu vật tư và danh sách mã thành phẩm cấu thành
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition-colors"
            title="Đóng (Esc)"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500 space-y-3">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
              <p className="text-xs font-medium">Đang tải thông tin truy vết nguồn gốc BOM...</p>
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-xs text-red-700 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-red-800">Không thể tải thông tin truy vết</div>
                <div className="mt-1">{error}</div>
              </div>
            </div>
          )}

          {data && !loading && (
            <>
              {/* Card 1: Thông tin vật tư & Đợt nhu cầu */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-lg border border-slate-200 bg-slate-50/50 p-4 text-xs">
                <div>
                  <span className="text-slate-500">Vật tư:</span>
                  <div className="font-semibold text-slate-900 text-sm mt-0.5">
                    {data.material_name}
                  </div>
                </div>

                <div>
                  <span className="text-slate-500">Đợt nhu cầu (Snapshot):</span>
                  <div className="font-semibold text-slate-900 text-sm mt-0.5 truncate" title={data.batch_title}>
                    {data.batch_title}
                  </div>
                  <div className="text-slate-500 text-[11px] mt-0.5">
                    Tạo ngày: <span className="font-medium text-slate-700">{formatDateFull(data.batch_created_at)}</span>
                  </div>
                </div>
              </div>

              {/* Card 2: So sánh số lượng đặt mua vs Định mức BOM */}
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold text-slate-800 mb-3 flex items-center justify-between">
                  <span>Đối chiếu số lượng mua & Định mức</span>
                  <span className="text-[11px] font-normal text-slate-500">Đơn vị: {data.order_uom}</span>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="rounded border border-slate-200 bg-slate-50 p-2.5">
                    <div className="text-[11px] text-slate-500">Định mức BOM (Gross)</div>
                    <div className="text-base font-bold text-slate-800 font-mono mt-1">
                      {formatVnNumber(data.qty_gross_bom)} <span className="text-xs font-normal text-slate-500">{data.order_uom}</span>
                    </div>
                  </div>

                  <div className="rounded border border-blue-200 bg-blue-50/50 p-2.5">
                    <div className="text-[11px] text-blue-700 font-medium">SL đặt trên PO</div>
                    <div className="text-base font-bold text-blue-900 font-mono mt-1">
                      {formatVnNumber(data.order_qty)} <span className="text-xs font-normal text-blue-600">{data.order_uom}</span>
                    </div>
                  </div>

                  <div className={`rounded border p-2.5 ${
                    data.variance_qty === 0
                      ? 'border-emerald-200 bg-emerald-50/50 text-emerald-800'
                      : data.variance_qty > 0
                      ? 'border-amber-200 bg-amber-50/50 text-amber-800'
                      : 'border-purple-200 bg-purple-50/50 text-purple-800'
                  }`}>
                    <div className="text-[11px]">Chênh lệch thực tế</div>
                    <div className="text-base font-bold font-mono mt-1">
                      {data.variance_qty > 0 ? `+${formatVnNumber(data.variance_qty)}` : formatVnNumber(data.variance_qty)}{' '}
                      <span className="text-xs font-normal">{data.order_uom}</span>
                    </div>
                  </div>
                </div>

                {/* Explanation / Notes */}
                <div className="mt-3 text-xs">
                  {data.variance_qty === 0 ? (
                    <div className="flex items-center gap-1.5 text-emerald-700">
                      <CheckCircle2 className="h-4 w-4 shrink-0" />
                      <span>Số lượng đặt mua khớp chính xác 100% với định mức tính toán từ BOM.</span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-1.5 text-amber-700">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <div>
                        <span>
                          {data.variance_qty > 0
                            ? `Số lượng mua thực tế cao hơn định mức BOM (+${formatVnNumber(data.variance_qty)} ${data.order_uom}) do điều chỉnh làm tròn kiện/đóng gói hoặc mua dự phòng.`
                            : `Số lượng mua thực tế thấp hơn định mức BOM (${formatVnNumber(data.variance_qty)} ${data.order_uom}) do tận dụng tồn kho có sẵn.`}
                        </span>
                        {data.variance_note && (
                          <div className="mt-1 text-slate-600 italic">
                            Ghi chú điều chỉnh: "{data.variance_note}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Card 3: Bảng Breakdown cơ cấu đóng góp từ các mã hàng */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                    <Layers className="h-4 w-4 text-blue-600" />
                    Cơ cấu đóng góp theo mã thành phẩm ({data.breakdown?.length ?? 0} mã hàng)
                  </div>
                  <span className="text-[11px] text-slate-500">
                    Bảo toàn từng dòng — Không gộp ô (No merged cells)
                  </span>
                </div>

                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                      <tr>
                        <th className="py-2.5 px-3 text-center w-12">STT</th>
                        <th className="py-2.5 px-3 min-w-[130px]">Mã hàng</th>
                        <th className="py-2.5 px-3 min-w-[180px]">Tên sản phẩm</th>
                        <th className="py-2.5 px-3 text-center w-28">Nguồn BOM</th>
                        <th className="py-2.5 px-3 text-right w-36">SL cấu thành</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {!data.breakdown || data.breakdown.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-6 text-center text-slate-400">
                            Không có dữ liệu phân rã mã hàng
                          </td>
                        </tr>
                      ) : (
                        data.breakdown.map((item, idx) => (
                          <tr key={`${item.product_id}-${item.product_code}-${idx}`} className="hover:bg-slate-50/60 transition-colors">
                            <td className="py-2.5 px-3 text-center text-slate-400 font-mono">
                              {idx + 1}
                            </td>
                            <td className="py-2.5 px-3 font-semibold text-slate-900 font-mono">
                              {item.product_code}
                            </td>
                            <td className="py-2.5 px-3 text-slate-700">
                              {item.product_name || '—'}
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <StatusBadge
                                tone="neutral"
                                label={getBomSourceLabel(item.bom_source)}
                                showDot={false}
                              />
                            </td>
                            <td className="py-2.5 px-3 text-right font-mono font-medium text-slate-800">
                              {formatVnNumber(item.contribution_qty)}{' '}
                              <span className="text-[11px] text-slate-400 font-normal">{data.order_uom}</span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t border-slate-200 font-semibold text-slate-800">
                      <tr>
                        <td colSpan={4} className="py-2.5 px-3 text-right">
                          Tổng nhu cầu định mức (BOM Gross):
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-blue-900 font-bold">
                          {formatVnNumber(totalContribution)}{' '}
                          <span className="text-[11px] text-slate-500 font-normal">{data.order_uom}</span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end border-t border-slate-200 px-6 py-3.5 bg-slate-50">
          <Button variant="secondary" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  )
}
